import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import '../styles/globals.css';

const MethodologyContainer = styled.div`
  max-width: 100%;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--bg-page);
`;

const PageWrapper = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 24px 4rem 24px;

  @media (max-width: 768px) {
    padding: 0 16px 3rem 16px;
  }
`;

const TitleSection = styled.div`
  width: 100%;
  padding: 3rem 0 1.5rem 0;

  @media (max-width: 768px) {
    padding: 2rem 0 1rem 0;
  }
`;

const Title = styled.h1`
  font-family: var(--font-lora);
  font-weight: 600;
  color: var(--color-text);
  font-size: 2.5rem;
  line-height: 1.15;
  letter-spacing: -0.01em;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Content = styled.div`
  color: var(--color-text);
  font-size: 1.0625rem;
  line-height: 1.7;

  h2 {
    font-family: var(--font-lora);
    font-weight: 600;
    font-size: 1.5rem;
    margin: 2.5rem 0 1rem 0;
    color: var(--color-text);
  }

  p {
    margin-bottom: 1rem;
  }

  ol, ul {
    margin: 0 0 1rem 1.5rem;

    li {
      margin-bottom: 0.5rem;
    }
  }

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.9375rem;
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.1em 0.35em;
    border-radius: 3px;
  }

  a {
    color: var(--color-accent);
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      color: var(--color-accent-hover);
    }
  }

  .callout {
    background-color: rgba(202, 138, 4, 0.08);
    border-left: 4px solid #ca8a04;
    padding: 0.875rem 1rem;
    border-radius: 0 var(--radius-md) var(--radius-md) 0;
    margin: 1rem 0 1.5rem 0;
    font-size: 0.9375rem;

    .callout-label {
      font-weight: 600;
      color: #92710c;
      font-size: 0.6875rem;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      display: block;
      margin-bottom: 0.25rem;
    }
  }

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

function Methodology() {
  return (
    <MethodologyContainer>
      <PageWrapper>
        <TitleSection>
          <Title>Methodology</Title>
        </TitleSection>

        <Content>
          <p>
            The Department of Labor's proposed prevailing-wage rule (<a
              href="https://www.regulations.gov/docket/ETA-2026-0001"
              target="_blank"
              rel="noopener noreferrer"
            >docket ETA-2026-0001</a>, RIN 1205-AC30) drew over a thousand unique public
            comments. Here's how we built this site to categorize them. If you find
            comments that were incorrectly tagged or inaccurately summarized,{' '}
            <Link to="/correction">let us know</Link>.
          </p>

          <ol>
            <li>
              <strong>Collect.</strong> We pull every public comment from
              regulations.gov, including attachments and in-line text. Many comments
              are near-identical form letters submitted by different people; we
              collapse each set into one representative and link to every copy, so
              the counts reflect distinct submissions rather than signature totals.
            </li>
            <li>
              <strong>Read and summarize.</strong> An LLM (Opus 4.8) reads each
              comment and the rule itself in full, then writes a plain-language
              summary and pulls one representative quote.
            </li>
            <li>
              <strong>Classify.</strong> The same model infers each commenter's type
              (an individual, an employer, a labor union, a university, and so on)
              and which of six recommended actions the comment urges, from finalizing
              the rule as written to withdrawing it outright.
            </li>
            <li>
              <strong>Tag by topic.</strong> A subject-matter expert defined a fixed
              set of 16 topics and 7 narrower subtopics that sit beneath them — for
              instance, "Impact on universities and research" sits under
              "Sector-specific impacts." The model tags every topic and subtopic a
              comment substantively engages. Tagging a subtopic also files the comment
              under its parent topic, so the broad topics always contain everything
              beneath them.
            </li>
            <li>
              <strong>Review and publish.</strong> We spot-checked the model's tags
              and summaries against the underlying comments, then published the result
              as this searchable, filterable database.
            </li>
          </ol>

          <h2>Limitations</h2>
          <ul>
            <li>
              The labels and summaries reflect the model's reading of each comment,
              not the commenter's own words.
            </li>
            <li>
              Topic tags are the model's judgment. A reasonable reader might file a
              given comment under different topics than it did.
            </li>
            <li>
              A few people uploaded their attachment as a scanned image or photo of
              a document rather than as digital text. Our automated reader pulls text
              from digital files, but it can't read words that appear only inside an
              image — so for a handful of comments, the summary and tags drew on less
              of the attached document than it actually contains. Those comments are
              still included, and the original attachment is posted here so you can
              open and read it in full.
            </li>
          </ul>

          <p>
            Spot an error? Reach out to{' '}
            <a href="mailto:violet@ifp.org">violet@ifp.org</a> to let us know.
          </p>
        </Content>
      </PageWrapper>
    </MethodologyContainer>
  );
}

export default Methodology;
